import React, { useMemo, useContext } from "react";
import { useTranslation } from "react-i18next";
import { CartContext } from "./CartContext";
import { isGbpLanguage } from "../../constans/CurrencyConstants";
import "../../css/cart.scss";

const getLineKey = (it) =>
  it?.lineKey ||
  (it?.skuId != null ? `${it?.id}_${it.skuId}` : String(it?.id ?? ""));

const CartDrawerShippingIcon = () => (
  <svg
    className="w-5 h-5 shrink-0 cart-drawer-gold-icon"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4"
    />
  </svg>
);

const CartDrawerLockIcon = () => (
  <svg
    className="w-4 h-4 cart-drawer-gold-icon"
    fill="currentColor"
    viewBox="0 0 20 20"
  >
    <path
      fillRule="evenodd"
      d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
      clipRule="evenodd"
    />
  </svg>

);

const CartDrawer = ({
  open,
  onClose,
  items,
  image,
  title,
  options = [],
}) => {
  const { t, i18n } = useTranslation();
  const tr = (key, fallback) => {
    try {
      const v = t(key);
      return v === key ? (fallback ?? key) : v;
    } catch {
      return fallback ?? key;
    }
  };
  const { removeProduct, updateItemQty } = useContext(CartContext) || {};
  const canUpdateQty = typeof updateItemQty === "function";
  const canRemove = typeof removeProduct === "function";
  const formatEuro = useMemo(() => {
    return (n) => {
      const isUk = isGbpLanguage(i18n.language);
      const locale = isUk ? "en-GB" : "nl-NL";
      const currency = "EUR";
      const amount = n;
      return new Intl.NumberFormat(locale, {
        style: "currency",
        currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
        .format(amount)
        .replace(/\u00A0/g, " ");
    };
  }, [i18n.language]);

  if (!open) return null;

  const hasItems = Array.isArray(items) && items.length > 0;
  const list = hasItems ? items : [];

  const parseCartNumber = (v) => {
    if (typeof v === "number" && Number.isFinite(v)) return v;
    if (v == null || v === "") return NaN;
    if (typeof v === "string") {
      const s = v.replace(/\s/g, "").replace(",", ".");
      const n = Number(s);
      return Number.isFinite(n) ? n : NaN;
    }
    const n = Number(v);
    return Number.isFinite(n) ? n : NaN;
  };

  const total = hasItems
    ? items.reduce((sum, it) => {
      const p = parseCartNumber(it?.price);
      const qn = parseCartNumber(it?.qty);
      const qq = Number.isFinite(qn) ? qn : Number(it?.qty) || 0;
      if (!Number.isFinite(p)) return sum;
      return sum + p * qq;
    }, 0)
    : 0;

  /** 与站点「满 €250 免邮」一致；购物车金额按 EUR 计价 */
  const freeShipThresholdEur = 250;
  const qualifiesFreeShipping =
    freeShipThresholdEur > 0 && total >= freeShipThresholdEur;
  const remainingForFreeShipEur = Math.max(0, freeShipThresholdEur - total);
  /** 用 scaleX 画进度，避免 flex 下 width:% 失效或 NaN% 被忽略后子元素撑满整条轨道 */
  const freeShipRatio = Math.min(
    1,
    Math.max(0, freeShipThresholdEur > 0 ? total / freeShipThresholdEur : 1)
  );

  return (
    <>
      <div className="cart-overlay" onClick={onClose} />
      <aside className="cart-drawer" role="dialog" aria-label={t("drawer.ariaCart")}>
        <div className="cart-drawer__head">
          <h2 className="cart-drawer__title">{title}</h2>
          <button
            type="button"
            className="cart-close"
            onClick={onClose}
            aria-label={t("drawer.close")}
          >
            ✕
          </button>
        </div>

        {hasItems ? (
          <div className="cart-drawer__promo">
            <div className="cart-drawer__promoRow">
              <span className="cart-drawer__promoIcon" aria-hidden="true">
                <CartDrawerShippingIcon />
              </span>
              <div className="cart-drawer__promoMain">
                <p className="cart-drawer__promoText">
                  {qualifiesFreeShipping
                    ? t("drawer.freeShipping")
                    : t("drawer.freeShippingMore", {
                      amount: formatEuro(remainingForFreeShipEur),
                    })}
                </p>
                <div className="cart-drawer__progressTrack" aria-hidden="true">
                  <div
                    className="cart-drawer__progressFill"
                    style={{
                      transform: `scaleX(${freeShipRatio})`,
                      transformOrigin: "left center",
                    }}
                  />
                </div>
              </div>
            </div>

          </div>
        ) : null}

        <div className="cart-drawer__body">
          {!hasItems ? (
            <div className="cart-empty-drawer">
              {tr("drawer.empty", "Geen producten in je winkelwagen.")}
            </div>
          ) : (
            <>
              {list.map((it, i) => {
                const lineKey = getLineKey(it);
                const q = Number(it.qty) || 0;
                const unit = Number(it.price) || 0;
                const compareRaw =
                  Number(it.compareAtPrice) || Number(it.originalPrice) || 0;
                const showDiscount =
                  compareRaw > 0 && compareRaw > unit + 0.001;

                const bumpQty = (delta) => {
                  if (!canUpdateQty || !lineKey) return;
                  const next = Math.max(1, q + delta);
                  updateItemQty(lineKey, next);
                };

                return (
                  <article className="cart-drawer__card" key={`drawer-${lineKey || i}`}>
                    <div className="cart-drawer__cardTop">
                      <img
                        className="cart-item__thumb"
                        src={it.thumb || image}
                        alt={it.title || title}
                      />
                      <div className="cart-item__main">
                        <div className="cart-item__title">{it.title || title}</div>
                        <div className="cart-item__opts">
                          {Array.isArray(it.options)
                            ? it.options.map((op, j) => <div key={j}>{op}</div>)
                            : Array.isArray(options)
                              ? options.map((op, j) => <div key={j}>{op}</div>)
                              : null}
                        </div>
                        <div className="cart-item__rowPrice">
                          <span className="cart-item__qtyTimes">{q} × </span>
                          {showDiscount ? (
                            <span className="cart-item__pricePair">
                              <del className="cart-item__strike">
                                {formatEuro(compareRaw)}
                              </del>{" "}
                              <span className="cart-item__sale">
                                {formatEuro(unit)}
                              </span>
                            </span>
                          ) : (
                            <span className="cart-item__unitPrice">
                              {formatEuro(unit)}
                            </span>
                          )}
                        </div>
                        <div className="cart-drawer__cardFoot">
                          <div className="cart-qtyStepper">
                            <button
                              type="button"
                              className="cart-qtyStepper__btn"
                              aria-label={t("drawer.qtyDecrease")}
                              disabled={!canUpdateQty || !lineKey || q <= 1}
                              onClick={() => bumpQty(-1)}
                            >
                              −
                            </button>
                            <span className="cart-qtyStepper__val">{q}</span>
                            <button
                              type="button"
                              className="cart-qtyStepper__btn"
                              aria-label={t("drawer.qtyIncrease")}
                              disabled={!canUpdateQty || !lineKey}
                              onClick={() => bumpQty(1)}
                            >
                              +
                            </button>
                          </div>
                          <button
                            type="button"
                            className="cart-remove cart-remove--text"
                            aria-label={t("drawer.remove")}
                            onClick={() => {
                              if (canRemove && lineKey) removeProduct(lineKey);
                            }}
                            disabled={!canRemove || !lineKey}
                          >
                            {t("drawer.remove")}
                          </button>
                        </div>
                      </div>
                    </div>

                  </article>
                );
              })}
            </>
          )}
        </div>

        {hasItems ? (
          <div className="cart-drawer__foot">
            <div className="cart-sub">
              <span className="cart-sub__label">{t("drawer.subtotal")}</span>
              <strong className="cart-sub__amount">{formatEuro(total)}</strong>
            </div>
            <div className="cart-actions">
              <a href="/cart" className="cart-btn cart-btn--secondary">
                {t("drawer.viewCart")}
              </a>
              <a href="/checkout" className="cart-btn cart-btn--primary">
                {t("drawer.checkout")}
              </a>
            </div>
            <p className="cart-drawer__secure">
              <CartDrawerLockIcon />
              <span>{t("drawer.securePayments")}</span>
            </p>
          </div>
        ) : null}
      </aside>
    </>
  );
};

export default CartDrawer;
